// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Maps common file extensions to user-friendly display names.
 */
const EXTENSION_LABELS: Record<string, string> = {
  // Documents
  pdf: 'PDF',
  doc: 'Word',
  docx: 'Word',
  xls: 'Excel',
  xlsx: 'Excel',
  ppt: 'PowerPoint',
  pptx: 'PowerPoint',
  csv: 'CSV',
  txt: 'Text',
  html: 'HTML',
  htm: 'HTML',
  json: 'JSON',
  xml: 'XML',

  // Images
  jpg: 'JPEG',
  jpeg: 'JPEG',
  png: 'PNG',
  gif: 'GIF',
  webp: 'WebP',
  svg: 'SVG',
  bmp: 'BMP',
  tiff: 'TIFF',
  tif: 'TIFF',

  // Archives
  zip: 'ZIP',
  rar: 'RAR',
  '7z': '7Z',
  gz: 'GZIP',
  tar: 'TAR',
};

/**
 * Maps common MIME types to user-friendly display names.
 */
const MIME_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'PowerPoint',
  'text/csv': 'CSV',
  'text/plain': 'Text',
  'text/html': 'HTML',
  'application/json': 'JSON',
  'application/xml': 'XML',
  'text/xml': 'XML',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'image/svg+xml': 'SVG',
  'image/bmp': 'BMP',
  'image/tiff': 'TIFF',
  'application/zip': 'ZIP',
  'application/x-rar-compressed': 'RAR',
  'application/x-7z-compressed': '7Z',
  'application/gzip': 'GZIP',
  'application/x-tar': 'TAR',
  'application/octet-stream': 'Binary',
};

/**
 * Gets a friendly label for a mime type.
 */
export function getMimeTypeLabel(mimeType: string): string {
  return MIME_TYPE_LABELS[mimeType.toLowerCase()] ?? mimeType;
}

/**
 * Formats file type information based on the filename extension and mime type.
 * If the extension is well-known, returns "ExtLabel (MimeLabel)" (e.g., "CSV (Excel)").
 * If the extension is unknown, returns a friendly mime type label or the raw mime type.
 *
 * @param fileName - The name of the file (used to extract extension).
 * @param mimeType - The MIME type string from the server.
 */
export function formatFileType(
  fileName: string,
  mimeType: string | undefined
): string {
  // Extract extension from filename
  const lastDot = fileName.lastIndexOf('.');
  const extension =
    lastDot !== -1 ? fileName.slice(lastDot + 1).toLowerCase() : '';

  // Check for known extension
  const extensionLabel = EXTENSION_LABELS[extension];

  if (extensionLabel && mimeType) {
    const mimeLabel = getMimeTypeLabel(mimeType);
    // Don't show redundant info like "PNG (PNG)"
    if (extensionLabel === mimeLabel) {
      return extensionLabel;
    }
    return `${extensionLabel} (${mimeLabel})`;
  }

  // Fall back to friendly mime type label or unknown
  if (mimeType) {
    return getMimeTypeLabel(mimeType);
  }

  return 'Unknown';
}
